import React, { useState } from 'react';
import {
  Truck,
  Search,
  Filter,
  Plus,
  User,
  MapPin,
  Shield,
  Fuel,
  Navigation,
  Edit,
  Settings,
  Eye,
  CheckCircle,
  AlertTriangle,
  Clock
} from 'lucide-react';

const TrucksManagement = () => {
  // Datos simulados de camiones
  const [trucks] = useState([
    {
      id: 1,
      plateNumber: "ABC-123",
      model: "Mercedes Actros",
      driver: "Juan Pérez",
      status: "en_ruta",
      capacity: "40 tons",
      fuel: 85,
      currentLocation: { lat: 40.4168, lng: -3.7038 }, // Madrid
      destination: { lat: 41.3851, lng: 2.1734 }, // Barcelona
      route: "Madrid - Barcelona",
      estimatedArrival: "14:30",
      progress: 65,
      lastUpdate: new Date(),
      maintenance: "Próximo: 15/09/2024"
    },
    {
      id: 2,
      plateNumber: "DEF-456",
      model: "Volvo FH16",
      driver: "María García",
      status: "disponible",
      capacity: "35 tons",
      fuel: 92,
      currentLocation: { lat: 39.4699, lng: -0.3763 }, // Valencia
      destination: null,
      route: "Disponible en Valencia",
      estimatedArrival: null,
      progress: 0,
      lastUpdate: new Date(),
      maintenance: "Completado: 28/08/2024"
    },
    {
      id: 3,
      plateNumber: "GHI-789",
      model: "Scania R500",
      driver: "Carlos López",
      status: "mantenimiento",
      capacity: "45 tons",
      fuel: 45,
      currentLocation: { lat: 37.3891, lng: -5.9845 }, // Sevilla
      destination: null,
      route: "En taller - Sevilla",
      estimatedArrival: null,
      progress: 0,
      lastUpdate: new Date(),
      maintenance: "En proceso"
    },
    {
      id: 4,
      plateNumber: "JKL-012",
      model: "Man TGX",
      driver: "Ana Martínez",
      status: "en_ruta",
      capacity: "38 tons",
      fuel: 67,
      currentLocation: { lat: 43.2627, lng: -2.9253 }, // Bilbao
      destination: { lat: 42.8805, lng: -8.5456 }, // Santiago
      route: "Bilbao - Santiago",
      estimatedArrival: "16:45",
      progress: 35,
      lastUpdate: new Date(),
      maintenance: "Próximo: 10/10/2024"
    }
  ]);

  // eslint-disable-next-line no-unused-vars
  const getStatusColor = (status) => {
    switch (status) {
      case 'en_ruta': return '#3B82F6'
      case 'disponible': return '#10B981'
      case 'mantenimiento': return '#F59E0B'
      default: return '#6B7280'
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'en_ruta': return 'En Ruta'
      case 'disponible': return 'Disponible'
      case 'mantenimiento': return 'Mantenimiento'
      default: return status
    }
  };

  // Calcular estadísticas
  const stats = {
    total: trucks.length,
    enRuta: trucks.filter(t => t.status === 'en_ruta').length,
    disponible: trucks.filter(t => t.status === 'disponible').length,
    mantenimiento: trucks.filter(t => t.status === 'mantenimiento').length,
  };

  return (
    <div className="trucks-management">
      <header className="content-header">
        <div className="header-info">
          <h2>Gestión de Camiones</h2>
          <p>Control completo de la flota de camiones</p>
        </div>
        <div className="header-metrics">
          <div className="metric-item">
            <div className="metric-icon active">
              <Truck />
            </div>
            <div>
              <strong>{stats.enRuta}</strong>
              <span>En Ruta</span>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon available">
              <CheckCircle />
            </div>
            <div>
              <strong>{stats.disponible}</strong>
              <span>Disponibles</span>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon maintenance">
              <Settings />
            </div>
            <div>
              <strong>{stats.mantenimiento}</strong>
              <span>Mantenimiento</span>
            </div>
          </div>
        </div>
      </header>

      <div className="trucks-content">
        <div className="trucks-header">
          <div className="search-filters">
            <div className="search-wrapper">
              <Search className="search-icon" />
              <input type="text" placeholder="Buscar camiones..." className="search-input" />
            </div>
            <div className="filter-wrapper">
              <Filter className="filter-icon" />
              <select className="filter-select">
                <option value="">Todos los estados</option>
                <option value="en_ruta">En Ruta</option>
                <option value="disponible">Disponible</option>
                <option value="mantenimiento">Mantenimiento</option>
              </select>
            </div>
          </div>
          <button className="add-truck-btn">
            <Plus size={16} />
            <span>Añadir Camión</span>
          </button>
        </div>

        <div className="trucks-grid">
          {trucks.map(truck => (
            <div key={truck.id} className="truck-card glass-panel">
              <div className="truck-header">
                <div className="truck-avatar">
                  <Truck size={24} />
                </div>
                <div className="truck-info">
                  <h3>{truck.plateNumber}</h3>
                  <p>{truck.model}</p>
                </div>
                <div className={`truck-status ${truck.status}`}>
                  <span>{getStatusText(truck.status)}</span>
                </div>
              </div>

              <div className="truck-details">
                <div className="detail-row">
                  <User size={16} />
                  <span>Conductor: {truck.driver}</span>
                </div>
                <div className="detail-row">
                  <MapPin size={16} />
                  <span>{truck.route}</span>
                </div>
                <div className="detail-row">
                  <Shield size={16} />
                  <span>Capacidad: {truck.capacity}</span>
                </div>
                {truck.estimatedArrival && (
                  <div className="detail-row">
                    <Clock size={16} />
                    <span>Llegada estimada: {truck.estimatedArrival}</span>
                  </div>
                )}
                <div className="detail-row">
                  <Settings size={16} />
                  <span>Mantenimiento: {truck.maintenance}</span>
                </div>
              </div>

              <div className="truck-metrics">
                <div className="metric">
                  <div className="metric-label">
                    <Fuel size={14} />
                    <span>Combustible</span>
                  </div>
                  <div className="fuel-bar">
                    <div 
                      className="fuel-fill" 
                      style={{ 
                        width: `${truck.fuel}%`,
                        backgroundColor: truck.fuel > 50 ? '#10B981' : truck.fuel > 25 ? '#F59E0B' : '#EF4444'
                      }}
                    ></div>
                  </div>
                  <span className="fuel-percentage">{truck.fuel}%</span>
                </div>

                {truck.progress > 0 && (
                  <div className="metric">
                    <div className="metric-label">
                      <Navigation size={14} />
                      <span>Progreso</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${truck.progress}%` }}
                      ></div>
                    </div>
                    <span className="progress-percentage">{truck.progress}%</span>
                  </div>
                )}
              </div>

              <div className="truck-actions">
                <button className="action-btn primary">
                  <Eye size={14} />
                  Ver Ruta
                </button>
                <button className="action-btn secondary">
                  <Edit size={14} />
                  Editar
                </button>
                <button className="action-btn">
                  <Settings size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Resumen de Estado de Flota */}
        <div className="fleet-summary glass-panel">
          <div className="summary-header">
            <h3>Resumen de Estado de Flota</h3>
            <div className="summary-actions">
              <button className="summary-btn">
                <AlertTriangle size={16} />
                <span>Ver Alertas</span>
              </button>
            </div>
          </div>
          
          <div className="summary-grid">
            <div className="summary-card">
              <div className="summary-icon primary">
                <Truck size={24} />
              </div>
              <div className="summary-content">
                <div className="summary-value">{stats.total}</div>
                <div className="summary-label">Total Camiones</div>
                <div className="summary-trend positive">
                  <span>+2 este mes</span>
                </div>
              </div>
            </div>

            <div className="summary-card">
              <div className="summary-icon success">
                <CheckCircle size={24} />
              </div>
              <div className="summary-content">
                <div className="summary-value">{stats.disponible}</div>
                <div className="summary-label">Disponibles</div>
                <div className="summary-trend positive">
                  <span>{Math.round((stats.disponible / stats.total) * 100)}% de flota</span>
                </div>
              </div>
            </div>

            <div className="summary-card">
              <div className="summary-icon warning">
                <Settings size={24} />
              </div>
              <div className="summary-content">
                <div className="summary-value">{stats.mantenimiento}</div>
                <div className="summary-label">Mantenimiento</div>
                <div className="summary-trend neutral">
                  <span>Programado</span>
                </div>
              </div>
            </div>

            <div className="summary-card">
              <div className="summary-icon info">
                <Fuel size={24} />
              </div>
              <div className="summary-content">
                <div className="summary-value">
                  {Math.round(trucks.reduce((acc, t) => acc + t.fuel, 0) / trucks.length)}%
                </div>
                <div className="summary-label">Combustible Promedio</div>
                <div className="summary-trend positive">
                  <span>Nivel óptimo</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .trucks-management {
          padding: 24px;
          background: #f9fafb;
          min-height: 100vh;
        }

        .content-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          background: white;
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .header-info h2 {
          font-size: 24px;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .header-info p {
          color: #4a4a4a;
          margin: 0;
        }

        .header-metrics {
          display: flex;
          gap: 16px;
        }

        .metric-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .metric-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .metric-icon.active {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .metric-icon.available {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .metric-icon.maintenance {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .metric-item strong {
          display: block;
          font-size: 18px;
          font-weight: 700;
          color: #1a1a1a;
        }

        .metric-item span {
          font-size: 12px;
          color: #4a4a4a;
        }

        .trucks-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .search-filters {
          display: flex;
          gap: 12px;
          flex: 1;
        }

        .search-wrapper {
          position: relative;
          flex: 1;
        }

        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          width: 16px;
          height: 16px;
        }

        .search-input {
          width: 100%;
          padding: 10px 16px 10px 36px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          background: white;
        }

        .search-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .filter-wrapper {
          position: relative;
        }

        .filter-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          width: 16px;
          height: 16px;
        }

        .filter-select {
          padding: 10px 16px 10px 36px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          color: #1a1a1a;
          min-width: 200px;
        }

        .add-truck-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .add-truck-btn:hover {
          background: #2563eb;
          transform: translateY(-1px);
        }

        .glass-panel {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          border: 1px solid #e5e7eb;
          margin-bottom: 24px;
        }

        .trucks-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 20px;
          margin-bottom: 32px;
        }

        .truck-card {
          padding: 20px;
          transition: all 0.3s ease;
        }

        .truck-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .truck-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .truck-avatar {
          width: 40px;
          height: 40px;
          background: #3b82f6;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .truck-info {
          flex: 1;
        }

        .truck-info h3 {
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 2px 0;
        }

        .truck-info p {
          font-size: 12px;
          color: #94a3b8;
          margin: 0;
        }

        .truck-status {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .truck-status.en_ruta {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .truck-status.disponible {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .truck-status.mantenimiento {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .truck-details {
          margin-bottom: 16px;
        }

        .detail-row {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          font-size: 14px;
          color: #4a4a4a;
        }

        .detail-row svg {
          color: #94a3b8;
        }

        .truck-metrics {
          margin-bottom: 16px;
        }

        .metric {
          margin-bottom: 12px;
        }

        .metric-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #94a3b8;
          margin-bottom: 4px;
        }

        .fuel-bar, .progress-bar {
          height: 6px;
          background: #e2e8f0;
          border-radius: 3px;
          overflow: hidden;
          margin-bottom: 4px;
          position: relative;
        }

        .fuel-fill, .progress-fill {
          height: 100%;
          background: #3b82f6;
          transition: width 0.3s ease;
        }

        .fuel-percentage, .progress-percentage {
          font-size: 12px;
          color: #4a4a4a;
          font-weight: 500;
        }

        .truck-actions {
          display: flex;
          gap: 8px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #f9fafb;
          color: #4a4a4a;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.3s ease;
          flex: 1;
          justify-content: center;
        }

        .action-btn.primary {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        .action-btn.secondary {
          background: white;
          color: #4a4a4a;
        }

        .action-btn:hover {
          transform: translateY(-1px);
        }

        .action-btn.primary:hover {
          background: #2563eb;
        }

        .action-btn.secondary:hover {
          background: #f9fafb;
          color: #3b82f6;
          border-color: #3b82f6;
        }

        /* Fleet Summary */
        .fleet-summary {
          padding: 24px;
        }

        .summary-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .summary-header h3 {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
          color: #1a1a1a;
        }

        .summary-actions {
          display: flex;
          gap: 8px;
        }

        .summary-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #f9fafb;
          color: #4a4a4a;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .summary-btn:hover {
          background: white;
          color: #f59e0b;
          border-color: #f59e0b;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }

        .summary-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          transition: all 0.3s ease;
        }

        .summary-card:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          transform: translateY(-1px);
        }

        .summary-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .summary-icon.primary {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .summary-icon.success {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .summary-icon.warning {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .summary-icon.info {
          background: rgba(124, 58, 237, 0.1);
          color: #7c3aed;
        }

        .summary-content {
          flex: 1;
        }

        .summary-value {
          font-size: 24px;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .summary-label {
          font-size: 14px;
          color: #4a4a4a;
          margin-bottom: 4px;
        }

        .summary-trend {
          font-size: 12px;
          padding: 2px 8px;
          border-radius: 12px;
        }

        .summary-trend.positive {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .summary-trend.neutral {
          background: rgba(107, 114, 128, 0.1);
          color: #6b7280;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .trucks-grid {
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .content-header {
            flex-direction: column;
            align-items: stretch;
            gap: 16px;
          }

          .header-metrics {
            justify-content: space-between;
          }

          .trucks-header {
            flex-direction: column;
            align-items: stretch;
          }

          .trucks-grid {
            grid-template-columns: 1fr;
          }

          .summary-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 576px) {
          .summary-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default TrucksManagement;